import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, Loader2, Heart } from "lucide-react";
import { Reveal } from "./components";
import { QuoteCTA } from "./pages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function favSet() {
  try { return JSON.parse(localStorage.getItem("bhpc_favorites") || "[]"); } catch { return []; }
}

function ColorCard({ c }) {
  const [saved, setSaved] = useState(() => favSet().some((f) => f.code === c.code && f.brand === c.brand));
  const light = (c.lrv_estimated || 0) >= 55;
  const toggle = () => {
    const list = favSet();
    const exists = list.find((f) => f.code === c.code && f.brand === c.brand);
    const next = exists ? list.filter((f) => !(f.code === c.code && f.brand === c.brand))
      : [...list, { brand: c.brand, code: c.code, name: c.name, hex: c.hex }];
    localStorage.setItem("bhpc_favorites", JSON.stringify(next));
    setSaved(!exists);
  };
  return (
    <div data-testid={`color-card-${c.code.replace(/\s/g, "")}`} className="border border-black/10 bg-white group">
      <div className="h-32 relative" style={{ background: c.hex }}>
        <button onClick={toggle} aria-label="Save favorite" className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center ${saved ? "bg-gold text-white" : "bg-white/85 text-ink"}`}>
          <Heart size={14} fill={saved ? "currentColor" : "none"} />
        </button>
        <span className={`absolute bottom-2 left-2 text-[10px] font-mono px-1.5 py-0.5 ${light ? "bg-ink/10 text-ink" : "bg-white/85 text-ink"}`}>{c.hex}</span>
      </div>
      <div className="p-4">
        <p className="text-[10px] uppercase tracking-widest text-gold">{c.brand}</p>
        <p className="font-serif text-lg leading-tight mt-0.5">{c.name}</p>
        <p className="text-xs font-mono text-ink/50">{c.code}</p>
        <div className="flex flex-wrap gap-1.5 mt-3">
          <span className="text-[10px] uppercase tracking-wider border border-black/15 px-2 py-0.5">{c.undertone}</span>
          <span className="text-[10px] uppercase tracking-wider border border-black/15 px-2 py-0.5">{c.family}</span>
          <span className="text-[10px] uppercase tracking-wider border border-black/15 px-2 py-0.5">Est. LRV {c.lrv_estimated}</span>
        </div>
      </div>
    </div>
  );
}

export function ColorSearch() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [brand, setBrand] = useState("");
  const [family, setFamily] = useState("");
  const [undertone, setUndertone] = useState("");
  const [facets, setFacets] = useState({ families: [], brands: [], total: 0 });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => { axios.get(`${API}/colors/facets`).then((r) => setFacets(r.data)).catch(() => {}); }, []);

  const fetchColors = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: "48" });
      if (q) p.set("q", q);
      if (brand) p.set("brand", brand);
      if (family) p.set("family", family);
      if (undertone) p.set("undertone", undertone);
      const r = await axios.get(`${API}/colors?${p.toString()}`);
      setItems(r.data.items); setTotal(r.data.total);
    } catch { /* ignore */ }
    setLoading(false);
  }, [q, brand, family, undertone]);

  useEffect(() => { const t = setTimeout(fetchColors, 250); return () => clearTimeout(t); }, [fetchColors]);
  useEffect(() => { setParams(q ? { q } : {}); }, [q]);

  return (
    <div data-testid="color-search-page" className="pt-32 bg-ivory min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <Reveal>
          <p className="eyebrow text-gold">Color Library</p>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">Search {facets.total ? facets.total.toLocaleString() : ""} verified colors</h1>
          <p className="text-muted-foreground mt-4 max-w-2xl leading-relaxed">Every color below is a real, verified Sherwin-Williams or Benjamin Moore shade. Search by name or code, or filter by brand, family and undertone.</p>
        </Reveal>

        <div className="mt-8 grid md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/40" />
            <input data-testid="color-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or code…"
              className="w-full border border-black/15 bg-white pl-11 pr-4 py-3 text-base outline-none focus:border-gold" />
          </div>
          <select data-testid="filter-brand" value={brand} onChange={(e) => setBrand(e.target.value)} className="border border-black/15 bg-white px-4 py-3 text-sm outline-none focus:border-gold">
            <option value="">All Brands</option>
            {facets.brands.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <select data-testid="filter-family" value={family} onChange={(e) => setFamily(e.target.value)} className="border border-black/15 bg-white px-4 py-3 text-sm outline-none focus:border-gold">
            <option value="">All Families</option>
            {facets.families.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {["", "warm", "cool", "neutral"].map((u) => (
            <button key={u || "all"} data-testid={`filter-undertone-${u || "all"}`} onClick={() => setUndertone(u)}
              className={`text-xs uppercase tracking-wider border px-4 py-2 transition-colors ${undertone === u ? "bg-ink text-white border-ink" : "bg-white border-black/15 hover:border-gold"}`}>
              {u ? `${u} undertone` : "All undertones"}
            </button>
          ))}
        </div>

        <p className="text-sm text-ink/50 mt-6" data-testid="color-result-count">{loading ? "Searching…" : `${total.toLocaleString()} colors`}</p>

        {loading ? (
          <div className="py-24 text-center"><Loader2 className="animate-spin mx-auto text-gold" size={36} /></div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mt-4">
            {items.map((c) => <ColorCard key={`${c.brand}-${c.code}`} c={c} />)}
          </div>
        )}
      </div>
      <QuoteCTA />
    </div>
  );
}
